import React from "react";
import { Text, View } from "@react-pdf/renderer";

interface TagProps {
  type?: "green" | "blue" | "orange" | "yellow" | "gray" | "purple";
  children: React.ReactNode;
  backgroundColor?: string;
  pdfMode?: boolean;
  className?: string;
}

const Tag: React.FC<TagProps> = ({ type, children, backgroundColor, pdfMode = false, className }) => {
  const tagStyles = {
    green: {
      backgroundColor: "rgb(220, 252, 231)",
      color: "rgb(21, 128, 61)",
    },
    blue: {
      backgroundColor: "rgb(219, 234, 254)",
      color: "rgb(29, 78, 216)",
    },
    orange: {
      backgroundColor: "rgb(255, 237, 213)",
      color: "rgb(194, 65, 12)",
    },
    yellow: {
      backgroundColor: "rgb(254, 249, 195)",
      color: "rgb(161, 98, 7)",
    },
    gray: {
      backgroundColor: "rgb(249, 250, 251)",
      color: "rgb(55, 65, 81)",
    },
    purple: {
      backgroundColor: "rgb(243, 232, 255)",
      color: "rgb(109, 40, 217)",
    },
  };

  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const getLuminance = (r: number, g: number, b: number): number => {
    const [rs, gs, bs] = [r, g, b].map((c) => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const getTextColor = (bgColor: string): string => {
    let r, g, b;

    if (bgColor.startsWith("#")) {
      const rgb = hexToRgb(bgColor);
      if (!rgb) return "rgb(55, 65, 81)";
      r = rgb.r;
      g = rgb.g;
      b = rgb.b;
    } else if (bgColor.startsWith("rgb")) {
      const match = bgColor.match(/rgb\((\d+),?\s*(\d+),?\s*(\d+)\)/);
      if (!match) return "rgb(55, 65, 81)";
      r = parseInt(match[1]);
      g = parseInt(match[2]);
      b = parseInt(match[3]);
    } else {
      return "rgb(55, 65, 81)";
    }

    const luminance = getLuminance(r, g, b);
    const darkerR = Math.max(0, Math.floor(r * 0.3));
    const darkerG = Math.max(0, Math.floor(g * 0.3));
    const darkerB = Math.max(0, Math.floor(b * 0.3));

    return luminance > 0.5 ? `rgb(${darkerR}, ${darkerG}, ${darkerB})` : "rgb(255, 255, 255)";
  };

  let currentStyle: { backgroundColor: string; color: string };
  if (backgroundColor) {
    currentStyle = {
      backgroundColor: backgroundColor,
      color: getTextColor(backgroundColor),
    };
  } else {
    const defaultStyle = tagStyles.gray;
    currentStyle = tagStyles[type] || defaultStyle;
  }

  if (pdfMode) {
    return (
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 10,
          paddingVertical: 2,
          borderRadius: 10,
          backgroundColor: currentStyle.backgroundColor,
          alignSelf: "flex-start",
          width: "auto",
        }}>
        <Text
          style={{
            color: currentStyle.color,
            fontSize: 9,
            fontWeight: "bold",
          }}>
          {children}
        </Text>
      </View>
    );
  }

  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  const finalClassName = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <span
      className={finalClassName}
      style={{
        backgroundColor: currentStyle.backgroundColor,
        color: currentStyle.color,
      }}>
      {children}
    </span>
  );
};

export default Tag;
